import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  acknowledgeRegistration, addProof, closeRegistration, listProofs, listRegistrations, proofUrl, recordRegistration, registrationDraft,
} from '../lib/api';
import {
  copyText, dateOnly, dateTime, friendlyError, fromLocalInput, istToday, proofKindLabel, registrationChannelLabel, registrationStatusLabel,
  toLocalInput,
} from '../lib/format';
import type { Lead, ProofKind, Registration, RegistrationChannel, RegistrationProof } from '../lib/types';
import { Badge, Button, ErrorText, SelectField, TextArea, TextField } from './ui';

// Validity runs from the acknowledgement, or from the day it was sent until then (same rule as the database).
function validUntil(r: Registration): string {
  const from = new Date(new Date(r.acknowledged_at ?? r.sent_at).getTime() + 5.5 * 3600e3).toISOString().slice(0, 10);
  const d = new Date(`${from}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + r.valid_days);
  return d.toISOString().slice(0, 10);
}
function daysLeft(r: Registration): number {
  return Math.round((new Date(`${validUntil(r)}T00:00:00Z`).getTime() - new Date(`${istToday()}T00:00:00Z`).getTime()) / 86400e3);
}
const isLive = (r: Registration) => (r.status === 'sent' || r.status === 'acknowledged') && daysLeft(r) >= 0;

type Mode = null | 'new' | { ack: string } | { close: string } | { proof: string };

export function RegistrationPanel({ lead, onChanged }: { lead: Lead; onChanged: () => void }) {
  const [regs, setRegs] = useState<Registration[]>([]);
  const [proofs, setProofs] = useState<RegistrationProof[]>([]);
  const [mode, setMode] = useState<Mode>(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    const rs = await listRegistrations(lead.id).catch(() => []);
    setRegs(rs);
    setProofs(await listProofs(rs.map((r) => r.id)).catch(() => []));
  }, [lead.id]);
  useEffect(() => { load(); }, [load]);
  const done = async () => { setMode(null); await load(); onChanged(); };
  const live = regs.find(isLive);

  return (
    <section className="border-b border-line px-5 py-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted">Builder registration</h3>
        {!live && mode !== 'new' && <Button onClick={() => { setError(''); setMode('new'); }} disabled={!lead.project_id}>Register with builder</Button>}
      </div>
      {!lead.project_id && <p className="text-sm text-muted">Choose the project on this lead first.</p>}
      {lead.project_id && regs.length === 0 && mode !== 'new' && (
        <p className="text-sm text-warn">Not registered yet. Register the buyer with the builder before the site visit, so the builder credits your firm.</p>
      )}
      <ErrorText>{error}</ErrorText>
      {mode === 'new' && <NewRegistration lead={lead} onDone={done} onCancel={() => setMode(null)} />}

      <ul className="flex flex-col gap-3">
        {regs.map((r) => {
          const left = daysLeft(r);
          const open = r.status === 'sent' || r.status === 'acknowledged';
          const tone = !open ? 'bad' : left < 0 ? 'bad' : left <= 7 ? 'warn' : 'ok';
          const mine = proofs.filter((p) => p.registration_id === r.id);
          return (
            <li key={r.id} className="rounded-xl border border-line p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-2">
                  <Badge tone={r.status === 'acknowledged' ? 'ok' : r.status === 'sent' ? 'warn' : 'bad'}>{registrationStatusLabel[r.status]}</Badge>
                  {open && <Badge tone={tone}>{left < 0 ? `Ended ${-left} day${left === -1 ? '' : 's'} ago` : `${left} day${left === 1 ? '' : 's'} left`}</Badge>}
                </span>
                <Link to={`/proof/${r.id}`} target="_blank" className="text-accent hover:underline">Proof pack</Link>
              </div>
              <p className="mt-1">
                Sent {dateTime(r.sent_at)} by {registrationChannelLabel[r.sent_via]}{r.sent_to ? ` to ${r.sent_to}` : ''}
                {r.acknowledged_at && <> · acknowledged {dateTime(r.acknowledged_at)}{r.builder_ref ? ` (ref ${r.builder_ref})` : ''}</>}
              </p>
              <p className="text-muted">Valid until {dateOnly(validUntil(r))} ({r.valid_days} days from {r.acknowledged_at ? 'acknowledgement' : 'sending; restarts when acknowledged'})</p>
              {r.closed_reason && <p className="text-bad">{r.closed_reason}</p>}
              {mine.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                  {mine.map((p) => (
                    <li key={p.id}>
                      <button className="text-accent hover:underline" onClick={async () => {
                        try { window.open(await proofUrl(p.file_path), '_blank', 'noopener'); } catch (e) { setError(friendlyError(e)); }
                      }}>{proofKindLabel[p.kind]}</button>
                      <span className="text-muted"> · {dateTime(p.added_at)}{p.note ? ` · ${p.note}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              {open && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.status === 'sent' && <Button onClick={() => setMode({ ack: r.id })}>Builder acknowledged</Button>}
                  <Button onClick={() => setMode({ proof: r.id })}>Add proof</Button>
                  <Button variant="ghost" onClick={() => setMode({ close: r.id })}>Rejected or withdrawn</Button>
                </div>
              )}
              {typeof mode === 'object' && mode && 'ack' in mode && mode.ack === r.id && <AckForm reg={r} onDone={done} onCancel={() => setMode(null)} />}
              {typeof mode === 'object' && mode && 'close' in mode && mode.close === r.id && <CloseForm reg={r} onDone={done} onCancel={() => setMode(null)} />}
              {typeof mode === 'object' && mode && 'proof' in mode && mode.proof === r.id && <ProofForm reg={r} onDone={done} onCancel={() => setMode(null)} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function useAction(onDone: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = (fn: () => Promise<unknown>) => async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try { await fn(); await onDone(); } catch (err) { setError(friendlyError(err)); setBusy(false); }
  };
  return { busy, error, run };
}

function FormButtons({ busy, label, onCancel }: { busy: boolean; label: string; onCancel: () => void }) {
  return (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : label}</Button>
    </div>
  );
}

function NewRegistration({ lead, onDone, onCancel }: { lead: Lead; onDone: () => Promise<void>; onCancel: () => void }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [days, setDays] = useState(30);
  const [via, setVia] = useState<RegistrationChannel>('email');
  const [at, setAt] = useState(toLocalInput(new Date()));
  const [copied, setCopied] = useState(false);
  const [loadError, setLoadError] = useState('');
  const { busy, error, run } = useAction(onDone);
  useEffect(() => {
    registrationDraft(lead.id).then((d) => { setTo(d.to ?? ''); setSubject(d.subject); setBody(d.body); setDays(d.valid_days); })
      .catch((e) => setLoadError(friendlyError(e)));
  }, [lead.id]);
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <form onSubmit={run(() => recordRegistration({ leadId: lead.id, sentAt: fromLocalInput(at), via, to, body, validDays: days }))}
      className="mb-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <ErrorText>{loadError}</ErrorText>
      <p className="text-sm">1. Send this to the builder. 2. Record it here as sent. The time it was sent is your proof if another partner claims this buyer.</p>
      <TextField label="Builder's email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="cp.registrations@builder.com"
        hint="Save it on the project page so it fills in every time." />
      <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
      <TextArea label="Message" rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <a href={mailto} className="inline-flex items-center rounded-lg border border-line bg-surface px-3.5 py-2 text-sm font-medium hover:border-muted">Open in email</a>
        <Button type="button" onClick={async () => { await copyText(`${subject}\n\n${body}`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>{copied ? 'Copied' : 'Copy message'}</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <SelectField label="Sent by" value={via} onChange={(e) => setVia(e.target.value as RegistrationChannel)}>
          {Object.entries(registrationChannelLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </SelectField>
        <TextField label="Sent at (IST)" type="datetime-local" required value={at} onChange={(e) => setAt(e.target.value)} />
        <TextField label="Valid for (days)" type="number" min={1} max={365} required value={days} onChange={(e) => setDays(Number(e.target.value))} />
      </div>
      <ErrorText>{error}</ErrorText>
      <FormButtons busy={busy} label="Record as sent" onCancel={onCancel} />
    </form>
  );
}

function AckForm({ reg, onDone, onCancel }: { reg: Registration; onDone: () => Promise<void>; onCancel: () => void }) {
  const [at, setAt] = useState(toLocalInput(new Date()));
  const [ref, setRef] = useState('');
  const { busy, error, run } = useAction(onDone);
  return (
    <form onSubmit={run(() => acknowledgeRegistration(reg.id, fromLocalInput(at), ref))} className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Acknowledged at (IST)" type="datetime-local" required value={at} onChange={(e) => setAt(e.target.value)} />
        <TextField label="Builder's registration number" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="KD-REG-5521" />
      </div>
      <p className="text-xs text-muted">Add a screenshot of the builder's reply with "Add proof" afterwards.</p>
      <ErrorText>{error}</ErrorText>
      <FormButtons busy={busy} label="Save acknowledgement" onCancel={onCancel} />
    </form>
  );
}

function CloseForm({ reg, onDone, onCancel }: { reg: Registration; onDone: () => Promise<void>; onCancel: () => void }) {
  const [status, setStatus] = useState<'rejected' | 'withdrawn'>('rejected');
  const [reason, setReason] = useState('');
  const { busy, error, run } = useAction(onDone);
  return (
    <form onSubmit={run(() => closeRegistration(reg.id, status, reason))} className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <SelectField label="What happened" value={status} onChange={(e) => setStatus(e.target.value as 'rejected' | 'withdrawn')}>
        <option value="rejected">The builder rejected it</option>
        <option value="withdrawn">We withdrew it</option>
      </SelectField>
      <TextField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Builder says another partner registered the buyer first" />
      <ErrorText>{error}</ErrorText>
      <FormButtons busy={busy} label="Close registration" onCancel={onCancel} />
    </form>
  );
}

function ProofForm({ reg, onDone, onCancel }: { reg: Registration; onDone: () => Promise<void>; onCancel: () => void }) {
  const { firm } = useAuth();
  const [kind, setKind] = useState<ProofKind>(reg.status === 'acknowledged' ? 'acknowledgement' : 'sent_copy');
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const { busy, error, run } = useAction(onDone);
  return (
    <form onSubmit={run(async () => { if (!file || !firm) throw new Error('Choose a file first.'); await addProof(firm.id, reg.id, kind, file, note); })}
      className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <SelectField label="What is it" value={kind} onChange={(e) => setKind(e.target.value as ProofKind)}>
        {Object.entries(proofKindLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </SelectField>
      <label className="flex flex-col gap-1 text-sm font-medium">
        File (PDF or image, up to 10 MB)
        <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm font-normal" />
      </label>
      <TextField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Screenshot of builder's email reply" />
      <p className="text-xs text-muted">Proof files are kept as they are: they cannot be changed or removed later.</p>
      <ErrorText>{error}</ErrorText>
      <FormButtons busy={busy} label="Add proof" onCancel={onCancel} />
    </form>
  );
}
